/* Product media gallery — keyboard a11y for thumb buttons, clean swiper teardown,
   and smooth slide-to on variant-change re-hydrates.
   Main<->thumbs binding is handled natively via the `thumbs-swiper` attribute on the
   main <swiper-container> (Swiper Element's thumbs module). */

// Tracks which section ids have already mounted once this page load. First mount
// for a section snaps instantly; later mounts (hydrate-sections-cache swaps on
// variant change) animate so the user sees the gallery scroll to the new variant.
window.__pmgMounted = window.__pmgMounted || new Set();

if (!customElements.get('product-media-gallery')) {
  customElements.define(
    'product-media-gallery',
    class ProductMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mainEl = null;
        this.thumbsEl = null;
        this.rafId = null;
        this.attempts = 0;
        this.maxAttempts = 60;
        this._thumbButtonHandlers = null;
      }

      connectedCallback() {
        this.mainEl = this.querySelector('[data-role="main"]');
        this.thumbsEl = this.querySelector('[data-role="thumbs"]');
        if (!this.mainEl) return;
        this.waitForMain();
      }

      disconnectedCallback() {
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }
        if (this._thumbButtonHandlers) {
          this._thumbButtonHandlers.forEach(({ btn, handler }) => {
            btn.removeEventListener('click', handler);
          });
          this._thumbButtonHandlers = null;
        }
        try {
          this.thumbsEl?.swiper?.destroy(true, true);
        } catch (e) {
          /* already torn down */
        }
        try {
          this.mainEl?.swiper?.destroy(true, true);
        } catch (e) {
          /* already torn down */
        }
        this.mainEl = null;
        this.thumbsEl = null;
      }

      waitForMain() {
        if (this.mainEl?.swiper) {
          this.wireKeyboardActivation();
          this.wireActiveThumbCentering();
          this.applyInitialSlide();
          return;
        }
        if (this.attempts >= this.maxAttempts) return;
        this.attempts += 1;
        this.rafId = requestAnimationFrame(() => this.waitForMain());
      }

      wireActiveThumbCentering() {
        // When thumbs overflow, scroll them so the active thumb sits around the
        // middle of the visible window — but only once the active has advanced
        // past the midpoint. While the active is still in the first half, we
        // leave scrollTop at 0 so earlier thumbs stay visible above it. Same at
        // the far end: clamp so we don't reveal empty space past the last thumb.
        const mainSwiper = this.mainEl.swiper;
        if (!mainSwiper) return;
        mainSwiper.on('slideChange', () => {
          this.scrollThumbsForActive(mainSwiper.activeIndex, 400);
        });

        // Mirror Swiper's lock state onto the host element so CSS can hide the nav
        // arrows whenever the thumbs track can't scroll (swiper-button-lock isn't
        // always reliably applied to external navigation buttons).
        const syncLockState = () => {
          const locked = !!this.thumbsEl?.swiper?.isLocked;
          this.toggleAttribute('data-thumbs-locked', locked);
        };
        const thumbsSwiper = this.thumbsEl?.swiper;
        if (thumbsSwiper) {
          syncLockState();
          thumbsSwiper.on('lock', syncLockState);
          thumbsSwiper.on('unlock', syncLockState);
          thumbsSwiper.on('resize', syncLockState);
          thumbsSwiper.on('update', syncLockState);
        }
      }

      applyInitialSlide() {
        const targetIndex = parseInt(this.dataset.initialSlide || '0', 10);
        if (!Number.isFinite(targetIndex) || targetIndex < 0) return;
        const sectionId = this.dataset.sectionId;
        const isRehydrate = sectionId && window.__pmgMounted.has(sectionId);
        if (sectionId) window.__pmgMounted.add(sectionId);
        const speed = isRehydrate ? 500 : 0;
        try {
          this.mainEl.swiper.slideTo(targetIndex, speed, false);
          this.scrollThumbsForActive(targetIndex, speed);
        } catch (e) {
          /* swiper not ready yet or already destroyed */
        }
      }

      scrollThumbsForActive(active, speed = 400) {
        const s = this.thumbsEl?.swiper;
        if (!s || s.isLocked) return;
        const slideSize = (s.slidesSizesGrid?.[0] || 0) + (s.params?.spaceBetween || 0);
        const containerSize = s.isHorizontal() ? s.width : s.height;
        if (!slideSize || !containerSize) {
          s.slideTo(active, speed);
          return;
        }
        const visibleCount = Math.max(1, Math.floor(containerSize / slideSize));
        const half = Math.floor(visibleCount / 2);
        const maxIndex = Math.max(0, s.slides.length - visibleCount);
        let target;
        if (active <= half) target = 0;
        else if (active - half >= maxIndex) target = maxIndex;
        else target = active - half;
        s.slideTo(target, speed);
      }

      wireKeyboardActivation() {
        // Swiper's internal `click` handler reads `clickedIndex` from pointer events
        // and won't fire on keyboard-dispatched click events. Wire the thumb buttons
        // directly so Tab+Enter/Space advances the main slider.
        const mainSwiper = this.mainEl.swiper;
        if (!mainSwiper) return;
        const thumbButtons = this.querySelectorAll('.product-media-gallery__thumb-button');
        this._thumbButtonHandlers = [];
        thumbButtons.forEach((btn, i) => {
          const handler = () => mainSwiper.slideTo(i);
          btn.addEventListener('click', handler);
          this._thumbButtonHandlers.push({ btn, handler });
        });
      }
    }
  );
}
